import React from "react";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import type { ExpiryStatus } from "@services/vessel/types";
import { convertUtcToUae, formatDateToDDMMYYYY } from "@utils/date-time";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

interface DocumentCardProps {
  "title"?: React.ReactNode;
  "fileName": string;
  "documentName"?: string;
  "startDate"?: string;
  "expiryDate"?: string;
  "expiryStatus"?: ExpiryStatus;
  "permanent"?: boolean;
  "className"?: string;
  "rootClassName"?: string;
  "uploadDate"?: string;
  "startDateTrans"?: string;
  "expiryDateTrans"?: string;
  "classNameTrans"?: string;
  "uploadDateTrans"?: string;
  "additionalFields"?: Record<string, React.ReactNode>;
  "onDownload"?: () => void;
  "onPreview"?: () => void;
  "testId"?: string;
  "data-testid"?: string;
  "flag"?: string;
  "flagTrans"?: string;
  "nocStatus"?: string;
  "nocStatusTrans"?: string;
  "documentSummary"?: string;
  "documentSummaryTrans"?: string;
  "documentType"?: string;
  "documentTypeTrans"?: string;
  "includeTime"?: boolean;
  "action"?: React.ReactNode;
}

const ExpiryStyles = (
  t: TFunction,
): Record<ExpiryStatus, { text?: string; label: string; background: string }> => ({
  EXPIRED: {
    text: "text-[#E1172F]",
    label: t(`${LANG_BASE}.certificates.expired`),
    background: "bg-[#FCE7EA]",
  },
  EXPIRING_SOON: {
    text: "text-[#9B6B1F]",
    label: t(`${LANG_BASE}.certificates.expiring-soon`),
    background: "bg-[#FBF4EC]",
  },
});

const LANG_BASE = "vessel-management.vessel-status-page";

const DocumentCard: React.FC<DocumentCardProps> = ({
  title,
  fileName,
  documentName,
  startDate,
  expiryDate,
  expiryStatus,
  permanent,
  className,
  rootClassName,
  uploadDate,
  startDateTrans,
  expiryDateTrans,
  classNameTrans,
  uploadDateTrans,
  documentSummary,
  documentSummaryTrans,
  documentType,
  documentTypeTrans,
  additionalFields,
  action,
  onDownload,
  onPreview,
  testId,
  "data-testid": dataTestId,
  flag,
  flagTrans,
  nocStatus,
  nocStatusTrans,
  includeTime = false,
}) => {
  const { t } = useTranslation();
  const expiryStyle = expiryStatus ? ExpiryStyles(t)[expiryStatus] : undefined;
  const shouldShowExpiry = Boolean(expiryDate) || permanent !== undefined;
  const expiryValue = (() => {
    if (permanent) return t("common.permanent");
    if (expiryDate) return formatDateToDDMMYYYY(expiryDate, includeTime);
    return "-";
  })();
  const additionalFieldEntries = Object.entries(additionalFields ?? {});

  const hasDocumentData = Boolean(
    additionalFieldEntries.length > 0 ||
      startDate ||
      expiryDate ||
      permanent ||
      className ||
      uploadDate ||
      flag ||
      nocStatus ||
      documentSummary ||
      documentType,
  );

  return (
    <div
      data-testid={dataTestId ?? testId}
      className={`border rounded p-4 w-full border-[#F1F2F4] ${expiryStyle?.background ?? rootClassName ?? ""}`}
    >
      {title && <h3 className="font-bold text-base mb-2 truncate">{title}</h3>}

      <div className="flex justify-start items-center mb-2 w-full">
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline break-all cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            if (onPreview) {
              onPreview();
            }
          }}
        >
          {documentName && documentName !== fileName
            ? `${documentName}${fileName ? `.${fileName.split(".").pop()}` : ""}`
            : fileName}
        </a>
        {onDownload && (
          <button
            onClick={onDownload}
            className="cursor-pointer text-left ml-auto"
            title="Download"
          >
            <FileDownloadOutlined />
          </button>
        )}
      </div>

      {hasDocumentData && (
        <p className={`text-sm ${expiryStyle?.text ?? "text-[#646A77]"} `}>
          {startDate && (
            <span className="block">
              {startDateTrans ??
                t("vessel-management.bunker-barge-page.certificate-from-date-in-view")}
              {formatDateToDDMMYYYY(startDate, true)}
            </span>
          )}
          {shouldShowExpiry && (
            <span className="block">
              {expiryDateTrans ?? t(`${LANG_BASE}.certificates.expire-date`)}: {expiryValue}
              {expiryStatus && (
                <span className={`text-sm font-bold ml-1 ${expiryStyle?.text}`}>
                  - {expiryStyle?.label}
                </span>
              )}
            </span>
          )}

          {className && (
            <span className="block">
              {classNameTrans ?? t(`${LANG_BASE}.certificates.class-name`)} {className}
            </span>
          )}
          {uploadDate && (
            <span className="block">
              {uploadDateTrans ?? t(`${LANG_BASE}.certificates.upload-date`)}
              {formatDateToDDMMYYYY(convertUtcToUae(uploadDate), true)}
            </span>
          )}
          {flag && (
            <span className="block">
              {flagTrans}
              {flag}
            </span>
          )}
          {nocStatus && (
            <span className="block">
              {nocStatusTrans}
              {nocStatus}
            </span>
          )}
          {documentType && (
            <span className="block">
              {documentTypeTrans}: {documentType}
            </span>
          )}
          {documentSummary && (
            <span className="block break-all">
              {documentSummaryTrans}: {documentSummary}
            </span>
          )}
          {additionalFieldEntries.map(([label, value]) => (
            <span className="block" key={label}>
              {label}: {value}
            </span>
          ))}
        </p>
      )}

      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};

export default DocumentCard;
